/**
 * UserAvatar / EntityAvatar
 * Unified avatar/icon component with role-based gradients and consistent sizing.
 *
 * - Pass `initials` for user avatars (owners, managers).
 * - Pass `icon` for entity icons (companies, branches, devices, etc.).
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type AvatarRole =
  | "owner"      // blue → purple
  | "manager"    // emerald → teal
  | "company"    // purple → pink
  | "branch"     // amber → orange
  | "device";    // cyan → blue

export type AvatarSize = "sm" | "md" | "lg" | "xl";

export const avatarGradients: Record<AvatarRole, string> = {
  owner:   "bg-gradient-to-br from-blue-500 to-purple-600",
  manager: "bg-gradient-to-br from-emerald-500 to-teal-600",
  company: "bg-gradient-to-br from-purple-500 to-pink-600",
  branch:  "bg-gradient-to-br from-amber-500 to-orange-600",
  device:  "bg-gradient-to-br from-cyan-500 to-blue-600",
};

const sizes: Record<AvatarSize, string> = {
  sm: "w-8  h-8  rounded-2xl text-xs",
  md: "w-10 h-10 rounded-2xl text-sm",
  lg: "w-12 h-12 rounded-2xl text-sm",
  xl: "w-14 h-14 rounded-2xl text-lg",
};

interface AvatarProps {
  role: AvatarRole;
  size?: AvatarSize;
  className?: string;
  /** User initials (e.g. "MG"). Mutually exclusive with `icon`. */
  initials?: string;
  /** Entity icon node (e.g. <Building2 />). Mutually exclusive with `initials`. */
  icon?: ReactNode;
}

export default function UserAvatar({
  initials,
  icon,
  role,
  size = "md",
  className,
}: AvatarProps) {
  return (
    <div
      className={cn(
        sizes[size],
        avatarGradients[role],
        "flex items-center justify-center text-white font-semibold flex-shrink-0",
        className,
      )}
    >
      {icon ?? initials}
    </div>
  );
}
